import { useSyncExternalStore } from 'react';
import { rpcOptional } from './rpc';
import { getSession } from './session';

/**
 * Member preferences — which Home view to open on, install nudges, whether they
 * opted into the community pulse.
 *
 * The server is the source of truth (member_prefs, one row per member, via the
 * prefs_get / prefs_set RPCs from P7). localStorage is a *cache for first paint
 * only*: choosing Tracker as your home screen should not make the app flash
 * Services for 400ms while a cold PostgREST wakes up. The server value always
 * wins once it lands, and the mirror is cleared on sign-out so one member's
 * choices never greet the next person on a shared phone.
 *
 * Until the migrations are applied, rpcOptional degrades to the mirror and the
 * preference simply stays on this device.
 */

const KEY = 'ihsanone_prefs';

export const HOME_VIEWS = ['services', 'tracker', 'updates'];

export const defaultPrefs = () => ({
  home_view: 'services',
  community_opt_in: false,
  install_state: {},
});

function normalise(raw) {
  const base = defaultPrefs();
  if (!raw || typeof raw !== 'object') return base;
  return {
    home_view: HOME_VIEWS.includes(raw.home_view) ? raw.home_view : base.home_view,
    community_opt_in: Boolean(raw.community_opt_in),
    install_state: (raw.install_state && typeof raw.install_state === 'object')
      ? raw.install_state
      : base.install_state,
  };
}

function readMirror() {
  try {
    return normalise(JSON.parse(window.localStorage.getItem(KEY)));
  } catch {
    return defaultPrefs();
  }
}

function writeMirror(prefs) {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(prefs));
  } catch {
    /* private mode — preferences last only for this session */
  }
}

export function clearPrefs() {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
  state = { prefs: defaultPrefs(), loading: false, synced: false };
  emit();
}

let state = { prefs: readMirror(), loading: true, synced: false };
const listeners = new Set();

function emit() {
  listeners.forEach((listener) => listener());
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return state;
}

let inflight = null;

/** Pull the server's copy once per app start. Safe to call repeatedly. */
export function loadPrefs() {
  if (inflight) return inflight;
  if (!getSession()) {
    state = { ...state, loading: false };
    emit();
    return Promise.resolve(state.prefs);
  }
  inflight = (async () => {
    const row = await rpcOptional('prefs_get', {});
    const server = Array.isArray(row) ? row[0] : row;
    if (server) {
      const merged = normalise(server);
      state = { prefs: merged, loading: false, synced: true };
      writeMirror(merged);
    } else {
      // Not deployed yet, or no row: keep what the device knows.
      state = { ...state, loading: false, synced: false };
    }
    emit();
    inflight = null;
    return state.prefs;
  })();
  return inflight;
}

/**
 * Set one preference. Optimistic: the UI moves immediately and the server call
 * follows, because a settings toggle that waits on a cold backend feels broken.
 */
export async function setPref(key, value) {
  const next = normalise({ ...state.prefs, [key]: value });
  state = { ...state, prefs: next };
  writeMirror(next);
  emit();
  await rpcOptional('prefs_set', { p_patch: { [key]: value } });
  return next;
}

export function getPrefs() {
  return state.prefs;
}

export function usePrefs() {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return { prefs: snapshot.prefs, loading: snapshot.loading, synced: snapshot.synced, setPref };
}
